import React, { Component } from "react"
import PropTypes from "prop-types"

import * as arrayHelpers from "../../helpers/arrayHelpers"
import { isOwnerManagerOrAdmin } from "../../helpers/activeMaintenanceHelpers"
import { newPolicyTemplate } from "../../constants/policies"

import MaintenancePolicy from "./MaintenancePolicy"
import { Table, Button, Input } from "semantic-ui-react"
import { withTranslation } from "react-i18next"
import { isFleetMaintenanceHubCanada } from "../../helpers/affiliationHelpers"
import * as userHelpers from "../../helpers/userHelpers"
import * as vehicleHelpers from "../../helpers/vehicleHelpers"
import { autoApproveUser } from "../../constants/users"

class MaintenancePolicies extends Component {
  static propTypes = {
    onDelete: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    policies: PropTypes.array.isRequired,
    services: PropTypes.array.isRequired,
    users: PropTypes.array.isRequired,
    vehicles: PropTypes.array.isRequired,

    allowActions: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    onRedirect: PropTypes.func,
    user: PropTypes.object,
    vehicle: PropTypes.object
  }

  static defaultProps = {
    allowActions: true,
    isSubmitting: false,
    onRedirect: () => console.log("MaintenancePolicies component should redirect"),
    user: null,
    vehicle: null
  }

  state = {
    currentlyEditingId: null,
    policies: this.initialPolicies(),
    searchQuery: "",
    enrichedPolicies: this.computeEnrichedPolicies(this.initialPolicies()),
    showNewPolicy: false
  }

  computeEnrichedPolicies(policies) {
    const { users, vehicles } = this.props
    const usersList = userHelpers.selectOptions([autoApproveUser].concat(users), { semantic: true })
    const userMap = new Map(usersList.map((user) => [user.value, user.text]))
    const vehicleNameMap = new Map(
      vehicles.map((v) => [
        v.id,
        vehicleHelpers.formattedName(v, { displayLicensePlateNumber: true, displayOtherID: true })
      ])
    )

    return policies.map((policy) => {
      let userName
      if (policy.auto_approvable) {
        userName = "Auto-Approve"
      } else {
        userName =
          policy.user_ids && policy.user_ids.length > 0
            ? policy.user_ids
                .map((userId) => userMap.get(userId) || "")
                .filter((name) => name !== "")
                .join(", ")
            : ""
      }
      const vehicleNames = (policy.vehicle_ids || [])
        .map((vehicle_id) => vehicleNameMap.get(vehicle_id) || "")
        .filter(Boolean)
        .join(", ")
      return { ...policy, userName, vehicleNames }
    })
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.policies !== this.state.policies ||
      prevProps.users !== this.props.users ||
      prevProps.vehicles !== this.props.vehicles
    ) {
      this.setState({ enrichedPolicies: this.computeEnrichedPolicies(this.state.policies) })
    }
  }

  addNewPolicy() {
    const { user, vehicle } = this.props
    const { policies } = this.state

    let newPolicy = { ...newPolicyTemplate }

    if (user) {
      newPolicy["user_ids"] = user.id
    }
    if (vehicle) {
      newPolicy["vehicle_ids"] = vehicle.id
    }

    return newPolicy
  }

  changeCurrentlyEditing(id) {
    this.setState({ currentlyEditingId: id })
  }

  handleCancel(policyId) {
    if (policyId === null) {
      this.removeNewPolicy()
    } else {
      this.setState({ currentlyEditingId: null })
    }
  }

  handleNewPolicy = () => {
    const { allowActions, onRedirect } = this.props

    if (allowActions) {
      this.setState({ showNewPolicy: true })
    } else {
      onRedirect()
    }
  }

  initialPolicies() {
    const { policies } = this.props

    return JSON.parse(JSON.stringify(policies))
  }

  isAddingNewPolicy() {
    const { policies } = this.state

    let isAdding = false

    policies.forEach(function (policy) {
      if (policy.id === null) {
        isAdding = true
      }
    })

    return isAdding
  }

  shouldDisplayButtons(policy) {
    const { allowActions } = this.props

    const { currentlyEditingId } = this.state

    if (!allowActions) {
      return false
    } else {
      // eslint-disable-next-line
      return (
        (!this.isAddingNewPolicy() && currentlyEditingId === null) ||
        currentlyEditingId === policy.id
      )
    }
  }

  removeNewPolicy() {
    this.setState({ showNewPolicy: false })
  }

  searchApprovers = (event) => {
    const escapedText = event.target.value.match(/[^\\]+/)
    this.setState({ searchQuery: (escapedText || [])[0] || "" })
  }

  renderMaintenancePolicies() {
    const { allowActions, isSubmitting, onDelete, onSubmit, services, language } = this.props
    const { currentlyEditingId, enrichedPolicies, searchQuery } = this.state

    const sortedPolicies = enrichedPolicies.sort((a, b) => {
      const dateA = a.updated_at
        ? new Date(a.updated_at)
        : a.created_at
        ? new Date(a.created_at)
        : null
      const dateB = b.updated_at
        ? new Date(b.updated_at)
        : b.created_at
        ? new Date(b.created_at)
        : null

      if (dateA === null && dateB === null) return 0
      if (dateA === null) return 1
      if (dateB === null) return -1

      return dateB - dateA
    })

    const filteredPolicies = sortedPolicies.filter(
      (policy) =>
        policy.userName.toLowerCase().includes(searchQuery?.toLowerCase?.() || "") ||
        policy.vehicleNames.toLowerCase().includes(searchQuery?.toLowerCase?.() || "")
    )

    if (this.state.showNewPolicy) {
      filteredPolicies.unshift(this.addNewPolicy())
    }

    return filteredPolicies.map((policy, index) => (
      <MaintenancePolicy
        allowActions={allowActions}
        displayButtons={this.shouldDisplayButtons(policy)}
        isEditing={policy.id === currentlyEditingId || policy.id === null}
        key={index}
        isSubmitting={isSubmitting}
        onCancel={this.handleCancel.bind(this)}
        onDelete={onDelete}
        onEdit={this.changeCurrentlyEditing.bind(this)}
        onSubmit={onSubmit}
        policy={policy}
        services={services}
        users={this.props.users}
        vehicles={this.props.vehicles}
        language={language}
      />
    ))
  }

  render() {
    const { t } = this.props
    return (
      <React.Fragment>
        <div>
          <Input
            className="search policy-search"
            icon="search"
            onChange={this.searchApprovers}
            placeholder={t("searchApproversLabel")}
          />
          {isOwnerManagerOrAdmin(this.props.currentUser) && (
            <Button
              size="tiny"
              floated="right"
              disabled={this.isAddingNewPolicy() || this.state.currentlyEditingId}
              onClick={this.handleNewPolicy}
            >
              {t("addNewPolicyLabel")}
            </Button>
          )}
        </div>
        <Table className="borderless">
          <Table.Header>
            <Table.Row>
              {[
                t("approvedByLabel"),
                t("common:vehiclesLabel"),
                isFleetMaintenanceHubCanada()
                  ? t("maxAmountLabel", { candian_dollar: "CAD" })
                  : t("maxAmountLabel", { candian_dollar: "$" }),
                t("servicesLabel"),
                t("updatedDateLabel"),
                ""
              ].map((s) => (
                <Table.HeaderCell singleLine key={s}>
                  {s}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>{this.renderMaintenancePolicies()}</Table.Body>
        </Table>
      </React.Fragment>
    )
  }
} // class MaintenancePolicies

export default withTranslation("settings")(MaintenancePolicies)
